import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Card, Spinner } from "react-bootstrap";
import { Button } from "react-bootstrap";
import { FaTrash } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { updateFerry, getFerry } from "../../store/slices/ferry";
import { getPlace } from "../../store/slices/place";
import { getVendor } from "../../store/slices/vendor";

const INITIAL_FORM = {
  from: "",
  to: "",
};

const CLASS_OPTIONS = [
  "Economy",
  "Premium",
  "Royal",
  "Luxury",
  "Business Class",
  "Deluxe",
  "VIP",
  "Fly Bridge Seat",
  "Upper Deck",
  "Lower Deck",
  "Silver",
  "Majesty",
  "Seat",
  "Chair",
  "Berth",
  "Cabin",
  "Room"
];

const calculateDuration = (departure, arrival) => {
  if (!departure || !arrival) return "";

  const dep = new Date(`1970-01-01T${departure}:00`);
  let arr = new Date(`1970-01-01T${arrival}:00`);

  // Handle overnight (arrival next day)
  if (arr < dep) {
    arr.setDate(arr.getDate() + 1);
  }

  const diffMs = arr - dep;
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs / (1000 * 60)) % 60);

  return `${hours}h ${minutes}m`;
};

const INITIAL_FERRY = {
  name: "",
  vendor: "",
  schedules: [
    {
      departureTime: "",
      arrivalTime: "",
      duration: "",
      classes: [{ className: "", price: "" }],
    },
  ],
};

const INITIAL_SCHEDULE = {
  departureTime: "",
  arrivalTime: "",
  duration: "",
  classes: [{ className: "", price: "" }],
};

const EditFerry = () => {
  const { id } = useParams();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form, setForm] = useState(INITIAL_FORM);
  const [ferries, setFerries] = useState([INITIAL_FERRY]);

  useEffect(() => {
    if (!id) return;

    dispatch(getFerry({ id }))
      .unwrap()
      .then((res) => {
        const ferry = res.ferryData[0];

        setForm({
          from: ferry?.from?._id || "",
          to: ferry?.to?._id || "",
        });

        const formattedFerries =
          ferry?.ferries?.length > 0
            ? ferry.ferries.map((f) => ({
              name: f.name || "",
              vendor: f.vendor?._id || "",
              schedules:
                f.schedules?.length > 0
                  ? f.schedules.map((s) => ({
                    departureTime: s.departureTime || "",
                    arrivalTime: s.arrivalTime || "",
                    duration: s.duration || "",
                    classes:
                      s.classes?.length > 0
                        ? s.classes.map((c) => ({
                          className: c.className || "",
                          price: c.price || "",
                        }))
                        : [{ className: "", price: "" }],
                  }))
                  : [INITIAL_SCHEDULE],
            }))
            : [INITIAL_FERRY];

        setFerries(formattedFerries);
      })
      .catch(() => {
        toast.error("Failed to fetch ferry details");
      });
  }, [id, dispatch]);

  const { placeList = [] } = useSelector((state) => state.place);

  const { vendorList } = useSelector((state) => state.vendor);

  const vendors = vendorList?.vendors || [];
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(getPlace());
    const fetchvendordata = () => {
      try {
        const item = { service: "FERRY" };
        dispatch(getVendor(item));
      } catch (error) {
        console.log(error);
      }
    };
    fetchvendordata();
  }, [dispatch]);

  /* ------------------ Route Handling ------------------ */

  const handleInputChange = ({ target: { name, value } }) => {
    const nameRegex = /^[a-zA-Z\s']*$/;
    let errorMsg = "";

    if (name === "name") {
      if (value && !nameRegex.test(value)) {
        errorMsg = "Only letters, spaces,allowed.";
      } else if (value.trim().length > 30) {
        errorMsg = "Maximum 30 characters allowed.";
      } else if (value.trim().length > 0 && value.trim().length < 3) {
        errorMsg = "Minimum 3 characters required.";
      }
    }

    setForm((prev) => ({ ...prev, [name]: value }));

    setErrors((prev) => ({
      ...prev,
      [name]: errorMsg,
    }));
  };

  /* ------------------ Schedule Handling ------------------ */
  const handleScheduleChange = (fIndex, sIndex, field, value) => {
    setFerries((prev) =>
      prev.map((ferry, i) =>
        i === fIndex
          ? {
            ...ferry,
            schedules: ferry.schedules.map((schedule, j) => {
              if (j !== sIndex) return schedule;

              const updated = { ...schedule, [field]: value };

              if (field === "departureTime" || field === "arrivalTime") {
                updated.duration = calculateDuration(
                  field === "departureTime" ? value : schedule.departureTime,
                  field === "arrivalTime" ? value : schedule.arrivalTime,
                );
              }

              return updated;
            }),
          }
          : ferry,
      ),
    );
  };

  const handleClassChange = (fIndex, sIndex, cIndex, field, value) => {
    setFerries((prev) =>
      prev.map((ferry, i) =>
        i === fIndex
          ? {
            ...ferry,
            schedules: ferry.schedules.map((schedule, j) =>
              j === sIndex
                ? {
                  ...schedule,
                  classes: schedule.classes.map((cls, k) =>
                    k === cIndex ? { ...cls, [field]: value } : cls,
                  ),
                }
                : schedule,
            ),
          }
          : ferry,
      ),
    );
  };

  const addFerryBlock = () => {
    setFerries((prev) => [...prev, INITIAL_FERRY]);
  };

  const removeFerryBlock = (index) => {
    if (ferries.length === 1) {
      toast.error("At least one ferry required");
      return;
    }

    setFerries((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFerryChange = (index, field, value) => {
    setFerries((prev) =>
      prev.map((ferry, i) =>
        i === index ? { ...ferry, [field]: value } : ferry,
      ),
    );
  };

  const addSchedule = (fIndex) => {
    setFerries((prev) =>
      prev.map((ferry, i) =>
        i === fIndex
          ? {
            ...ferry,
            schedules: [...ferry.schedules, INITIAL_SCHEDULE],
          }
          : ferry,
      ),
    );
  };

  const removeSchedule = (fIndex, sIndex) => {
    if (ferries[fIndex].schedules.length === 1) {
      toast.error("At least one timing must exist");
      return;
    }

    setFerries((prev) =>
      prev.map((ferry, i) =>
        i === fIndex
          ? {
            ...ferry,
            schedules: ferry.schedules.filter((_, j) => j !== sIndex),
          }
          : ferry,
      ),
    );
  };

  const addClass = (fIndex, sIndex) => {
    setFerries((prev) =>
      prev.map((ferry, i) =>
        i === fIndex
          ? {
            ...ferry,
            schedules: ferry.schedules.map((schedule, j) =>
              j === sIndex
                ? {
                  ...schedule,
                  classes: [
                    ...schedule.classes,
                    { className: "", price: "" },
                  ],
                }
                : schedule,
            ),
          }
          : ferry,
      ),
    );
  };

  const removeClass = (fIndex, sIndex, cIndex) => {
    if (ferries[fIndex].schedules[sIndex].classes.length === 1) {
      toast.error("At least one class is required for this timing");
      return;
    }

    setFerries((prev) =>
      prev.map((ferry, i) =>
        i === fIndex
          ? {
            ...ferry,
            schedules: ferry.schedules.map((schedule, j) =>
              j === sIndex
                ? {
                  ...schedule,
                  classes: schedule.classes.filter((_, k) => k !== cIndex),
                }
                : schedule,
            ),
          }
          : ferry,
      ),
    );
  };

  /* ------------------ Validation ------------------ */

  const validateForm = () => {
    let isValid = true;
    const newErrors = {};

    if (!form.from) {
      newErrors.from = "Please select source";
      isValid = false;
    }

    if (!form.to) {
      newErrors.to = "Please select destination";
      isValid = false;
    }

    if (form.from === form.to) {
      newErrors.to = "Source and Destination cannot be same";
      isValid = false;
    }

    ferries.forEach((ferry, fIndex) => {
      if (!(ferry.name || "").trim()) {
        toast.error(`Enter ferry name for Ferry ${fIndex + 1}`);
        isValid = false;
      }

      if (!ferry.vendor) {
        toast.error(`Select vendor for Ferry ${fIndex + 1}`);
        isValid = false;
      }

      ferry.schedules.forEach((schedule) => {
        if (!schedule.departureTime) {
          toast.error("Departure time required");
          isValid = false;
        }

        if (!schedule.arrivalTime) {
          toast.error("Arrival time required");
          isValid = false;
        }

        schedule.classes.forEach((cls) => {
          if (!cls.className) {
            toast.error("Class is required");
            isValid = false;
          }

          if (!cls.price) {
            toast.error("Price is required");
            isValid = false;
          }
        });
      });
    });

    setErrors(newErrors);
    return isValid;
  };

  /* ------------------ Submit ------------------ */

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);

      const payload = {
        id,
        from: form.from,
        to: form.to,
        ferries: ferries.map((ferry) => ({
          name: (ferry.name || "").trim(),
          vendor: ferry.vendor,
          schedules: ferry.schedules.map((sch) => ({
            departureTime: sch.departureTime,
            arrivalTime: sch.arrivalTime,
            duration: sch.duration,
            classes: sch.classes.map((cls) => ({
              className: cls.className,
              price: Number(cls.price),
            })),
          })),
        })),
      };

      await dispatch(updateFerry(payload)).unwrap();

      toast.success("Ferry edited successfully");

      setForm(INITIAL_FORM);
      setErrors({});

      navigate("/admin/ferry");
    } catch (err) {
      toast.error(err || "Failed to Update ferry");
    } finally {
      setLoading(false);
    }
  };

  /* ------------------ UI ------------------ */

  return (
    <>
      <div className="breadcrumb-bar mb-3">
        <div className="breadcrumb-left">
          <Link to="/admin/home" className="breadcrumb-link active">
            Home
          </Link>
          |
          <Link to="/admin/ferry" className="breadcrumb-link active">
            Ferry
          </Link>
          | <span className="breadcrumb-link inactive">Edit Ferry</span>
        </div>
      </div>

      <Container fluid className="p-4">
        <Card className="shadow-sm border-0">
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              <Row className="mb-4">
                <Col md={3}>
                  <Form.Label>From</Form.Label>
                  <Form.Select
                    name="from"
                    value={form.from}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Location</option>
                    {placeList.map((place) => (
                      <option key={place._id} value={place._id}>
                        {place.name}
                      </option>
                    ))}
                  </Form.Select>
                  {errors.from && (
                    <small className="text-danger">{errors.from}</small>
                  )}
                </Col>

                <Col md={3}>
                  <Form.Label>To</Form.Label>
                  <Form.Select
                    name="to"
                    value={form.to}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Location</option>
                    {placeList
                      .filter((place) => place._id !== form.from)
                      .map((place) => (
                        <option key={place._id} value={place._id}>
                          {place.name}
                        </option>
                      ))}
                  </Form.Select>
                  {errors.to && (
                    <small className="text-danger">{errors.to}</small>
                  )}
                </Col>
              </Row>
              <Row>
                <Col md={10}>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5>Ferries</h5>

                    <button
                      type="button"
                      className="submit-btn"
                      onClick={addFerryBlock}
                    >
                      + Add Ferry
                    </button>
                  </div>
                  {ferries.map((ferry, fIndex) => (
                    <Card key={fIndex} className="p-3 mb-4 shadow-sm">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6>Ferry {fIndex + 1}</h6>

                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeFerryBlock(fIndex)}
                        >
                          <FaTrash /> Remove Ferry
                        </Button>
                      </div>

                      <Row className="mb-3">
                        <Col md={4}>
                          <Form.Label>Ferry Name</Form.Label>
                          <Form.Control
                            value={ferry.name}
                            placeholder="Enter ferry name"
                            onChange={(e) =>
                              handleFerryChange(fIndex, "name", e.target.value)
                            }
                          />
                        </Col>

                        <Col md={4}>
                          <Form.Label>Vendor</Form.Label>
                          <Form.Select
                            value={ferry.vendor}
                            onChange={(e) =>
                              handleFerryChange(
                                fIndex,
                                "vendor",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">Select</option>

                            {vendors?.map((vendor) => (
                              <option key={vendor._id} value={vendor._id}>
                                {vendor.name}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                      </Row>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">Ferry Timings</h5>

                        <button
                          type="button"
                          className="submit-btn"
                          onClick={() => addSchedule(fIndex)}
                        >
                          + Add Timing
                        </button>
                      </div>

                      {errors.schedulesError && (
                        <div className="text-danger mb-2">
                          {errors.schedulesError}
                        </div>
                      )}

                      {ferry.schedules.map((schedule, sIndex) => (
                        <div className="mb-4" key={sIndex}>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0">Timing {sIndex + 1}</h6>

                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeSchedule(fIndex, sIndex)}
                            >
                              <FaTrash className="me-1" />
                              Remove Timing
                            </Button>
                          </div>

                          {/* Card Content */}
                          <Card className="p-3 shadow-sm">
                            <Row className="mb-3">
                              <Col md={4}>
                                <Form.Label>Departure</Form.Label>
                                <Form.Control
                                  type="time"
                                  value={schedule.departureTime}
                                  onChange={(e) =>
                                    handleScheduleChange(
                                      fIndex,
                                      sIndex,
                                      "departureTime",
                                      e.target.value,
                                    )
                                  }
                                />
                                {errors?.schedules?.[sIndex]?.departureTime && (
                                  <small className="text-danger">
                                    {errors.schedules[sIndex].departureTime}
                                  </small>
                                )}
                              </Col>

                              <Col md={4}>
                                <Form.Label>Arrival</Form.Label>
                                <Form.Control
                                  type="time"
                                  value={schedule.arrivalTime}
                                  onChange={(e) =>
                                    handleScheduleChange(
                                      fIndex,
                                      sIndex,
                                      "arrivalTime",
                                      e.target.value,
                                    )
                                  }
                                />
                                {errors?.schedules?.[sIndex]?.arrivalTime && (
                                  <small className="text-danger">
                                    {errors.schedules[sIndex].arrivalTime}
                                  </small>
                                )}
                              </Col>

                              <Col md={4}>
                                <Form.Label>Duration</Form.Label>
                                <Form.Control
                                  value={schedule.duration}
                                  readOnly
                                  placeholder="Auto Calculated"
                                />
                              </Col>
                            </Row>
                            {errors?.schedules?.[sIndex]?.classesError && (
                              <div className="text-danger mb-2">
                                {errors.schedules[sIndex].classesError}
                              </div>
                            )}

                            {schedule.classes.map((cls, cIndex) => (
                              <Row
                                key={cIndex}
                                className="mb-2 align-items-end"
                              >
                                <Col md={4}>
                                  <Form.Label>Class</Form.Label>
                                  <Form.Select
                                    value={cls.className}
                                    onChange={(e) =>
                                      handleClassChange(
                                        fIndex,
                                        sIndex,
                                        cIndex,
                                        "className",
                                        e.target.value,
                                      )
                                    }
                                  >
                                    <option value="">Select</option>
                                    {CLASS_OPTIONS.map((option) => (
                                      <option key={option} value={option}>
                                        {option}
                                      </option>
                                    ))}
                                  </Form.Select>
                                  {errors?.schedules?.[sIndex]?.classes?.[
                                    cIndex
                                  ]?.className && (
                                      <small className="text-danger">
                                        {
                                          errors.schedules[sIndex].classes[cIndex]
                                            .className
                                        }
                                      </small>
                                    )}
                                </Col>

                                <Col md={4}>
                                  <Form.Label>Price</Form.Label>
                                  <Form.Control
                                    type="text"
                                    placeholder="Enter Price"
                                    value={cls.price}
                                    inputMode="numeric"
                                    maxLength={5}
                                    onChange={(e) => {
                                      let value = e.target.value;

                                      if (!/^\d*$/.test(value)) return;
                                      if (value && Number(value) > 20000)
                                        return;

                                      handleClassChange(
                                        fIndex,
                                        sIndex,
                                        cIndex,
                                        "price",
                                        value,
                                      );
                                    }}
                                  />

                                  {errors?.schedules?.[sIndex]?.classes?.[
                                    cIndex
                                  ]?.price && (
                                      <small className="text-danger">
                                        {
                                          errors.schedules[sIndex].classes[cIndex]
                                            .price
                                        }
                                      </small>
                                    )}
                                </Col>

                                <Col md={2}>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    className="mt-0"
                                    onClick={() =>
                                      removeClass(fIndex, sIndex, cIndex)
                                    }
                                  >
                                    <FaTrash />
                                  </Button>
                                </Col>
                              </Row>
                            ))}

                            <div className="d-flex justify-content-end mt-3 border-top pt-3">
                              <Button
                                variant="light"
                                className="border"
                                size="sm"
                                onClick={() => addClass(fIndex, sIndex)}
                              >
                                + Add Class
                              </Button>
                            </div>
                          </Card>
                        </div>
                      ))}
                    </Card>
                  ))}
                </Col>
              </Row>

              <div className="d-flex justify-content-center gap-3">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </button>

                <button type="submit" className="submit-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner size="sm" animation="border" /> Submitting...
                    </>
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default EditFerry;
